import React, { useState, useRef, useCallback } from 'react';
import { View, useWindowDimensions, Text, TouchableOpacity } from 'react-native';
import { NavigationContainer, createNavigationContainerRef, useNavigation, type LinkingOptions } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { M01SplashScreen } from './screens/M01SplashScreen';
import { M02LoginScreen } from './screens/M02LoginScreen';
import { M03HomeScreen } from './screens/M03HomeScreen';
import { M04ProductListScreen } from './screens/M04ProductListScreen';
import { M05ProductDetailScreen } from './screens/M05ProductDetailScreen';
import { M06SelectCustomerScreen } from './screens/M06SelectCustomerScreen';
import { M06ConfigureProductScreen } from './screens/M06ConfigureProductScreen';
import { M06InputCustomerScreen } from './screens/M06InputCustomerScreen';
import { M07ResultScreen } from './screens/M07ResultScreen';
import { M08CompareScreen } from './screens/M08CompareScreen';
import { M09PdfScreen } from './screens/M09PdfScreen';
import { M10SaveCustomerScreen } from './screens/M10SaveCustomerScreen';
import { M11HistoryScreen } from './screens/M11HistoryScreen';
import { M12ProfileScreen } from './screens/M12ProfileScreen';
import { M13ShareScreen } from './screens/M13ShareScreen';
import { M14NotesScreen } from './screens/M14NotesScreen';
import { M15CustomerListScreen } from './screens/M15CustomerListScreen';
import { M17FinancialSurveyScreen } from './screens/M17FinancialSurveyScreen';
import { M16CustomerDetailScreen } from './screens/M16CustomerDetailScreen';
import { AppBar, BottomNav, LogoIcon, Icon } from './components';
import { useAtomValue } from 'jotai';
import { illustrationInputAtom } from './atoms/illustrationAtoms';
import { RootStackParamList, AppRoute } from './types/navigation';
import { APP_BAR_HEIGHT, BOTTOM_NAV_HEIGHT, colors } from './theme';
import { styles } from './AppNavigatorStyle';

export type { RootStackParamList } from './types/navigation';
export { AppRoute };

const Stack = createStackNavigator<RootStackParamList>();
const navigationRef = createNavigationContainerRef<RootStackParamList>();


const SIDEBAR_ITEMS = [
  { id: AppRoute.M03HomeScreen, label: 'Trang chủ', icon: 'home' },
  { id: AppRoute.M04ProductListScreen, label: 'Tính nhanh TLMHBH', icon: 'calculate' },
  { id: AppRoute.M11HistoryScreen, label: 'Tài liệu minh họa', icon: 'description' },
  { id: AppRoute.M15CustomerListScreen, label: 'Quản lý khách hàng', icon: 'groups' },
  { id: AppRoute.M12ProfileScreen, label: 'Hồ sơ Tư vấn', icon: 'person' },
] as const;

export const linking: LinkingOptions<RootStackParamList> = {
  prefixes: typeof window !== 'undefined' ? [window.location.origin, '/'] : ['/'],
  config: {
    screens: {
      [AppRoute.M01SplashScreen]: 'loading',
      [AppRoute.M02LoginScreen]: 'login',
      [AppRoute.M03HomeScreen]: '',
      [AppRoute.M04ProductListScreen]: 'tinh-nhanh',
      [AppRoute.M05ProductDetailScreen]: 'chi-tiet-san-pham',
      [AppRoute.M06InputCustomerScreen]: 'minh-hoa/:illustrationId?/nhap-tt-kh',
      [AppRoute.M06SelectCustomerScreen]: 'minh-hoa/nhap-tt-kh/step1',
      [AppRoute.M06ConfigureProductScreen]: 'minh-hoa/:illustrationId?/nhap-minh-hoa',
      [AppRoute.M07ResultScreen]: 'minh-hoa/:illustrationId?/ket-qua',
      [AppRoute.M08CompareScreen]: 'so-sanh',
      [AppRoute.M09PdfScreen]: 'pdf',
      [AppRoute.M10SaveCustomerScreen]: 'luu-khach-hang',
      [AppRoute.M11HistoryScreen]: 'minh-hoa',
      [AppRoute.M15CustomerListScreen]: 'khach-hang',
      [AppRoute.M16CustomerDetailScreen]: 'khach-hang/:customerId',
      [AppRoute.M17FinancialSurveyScreen]: 'khao-sat-nhu-cau',
    },
  },
};

const ROUTE_PATH_MAP: Record<string, string> = {
  [AppRoute.M01SplashScreen]: '/loading',
  [AppRoute.M02LoginScreen]: '/login',
  [AppRoute.M03HomeScreen]: '/trang-chu',
  [AppRoute.M04ProductListScreen]: '/tinh-nhanh',
  [AppRoute.M05ProductDetailScreen]: '/chi-tiet-san-pham',
  [AppRoute.M06InputCustomerScreen]: '/minh-hoa/nhap-tt-kh',
  [AppRoute.M06SelectCustomerScreen]: '/minh-hoa/nhap-tt-kh/step1',
  [AppRoute.M06ConfigureProductScreen]: '/minh-hoa/nhap-minh-hoa',
  [AppRoute.M07ResultScreen]: '/minh-hoa/ket-qua',
  [AppRoute.M08CompareScreen]: '/so-sanh',
  [AppRoute.M09PdfScreen]: '/pdf',
  [AppRoute.M10SaveCustomerScreen]: '/luu-khach-hang',
  [AppRoute.M11HistoryScreen]: '/minh-hoa',
  [AppRoute.M12ProfileScreen]: '/ho-so',
  [AppRoute.M13ShareScreen]: '/share',
  [AppRoute.M14NotesScreen]: '/crm-notes',
  [AppRoute.M15CustomerListScreen]: '/khach-hang',
  [AppRoute.M16CustomerDetailScreen]: '/khach-hang',
  [AppRoute.M17FinancialSurveyScreen]: '/khao-sat-nhu-cau',
};

const PAGE_TITLE_MAP: Record<string, string> = {
  [AppRoute.M01SplashScreen]: 'Đang tải...',
  [AppRoute.M02LoginScreen]: 'Đăng nhập',
  [AppRoute.M03HomeScreen]: 'Trang chủ',
  [AppRoute.M04ProductListScreen]: 'Lựa chọn sản phẩm',
  [AppRoute.M05ProductDetailScreen]: 'Chi tiết sản phẩm',
  [AppRoute.M06InputCustomerScreen]: 'Nhập thông tin khách hàng',
  [AppRoute.M06SelectCustomerScreen]: 'Chọn khách hàng',
  [AppRoute.M06ConfigureProductScreen]: 'Cấu hình minh họa',
  [AppRoute.M07ResultScreen]: 'Kết quả minh họa',
  [AppRoute.M08CompareScreen]: 'So sánh phương án',
  [AppRoute.M09PdfScreen]: 'Tài liệu PDF',
  [AppRoute.M10SaveCustomerScreen]: 'Lưu khách hàng',
  [AppRoute.M11HistoryScreen]: 'Danh sách bảng minh họa',
  [AppRoute.M12ProfileScreen]: 'Hồ sơ cá nhân',
  [AppRoute.M13ShareScreen]: 'Chia sẻ minh họa',
  [AppRoute.M14NotesScreen]: 'Ghi chú CRM',
  [AppRoute.M15CustomerListScreen]: 'Quản lý khách hàng',
  [AppRoute.M16CustomerDetailScreen]: 'Chi tiết khách hàng',
  [AppRoute.M17FinancialSurveyScreen]: 'Khảo sát nhu cầu tài chính',
};

export const getDocumentTitle = (screenName: string): string => {
  const pageName = PAGE_TITLE_MAP[screenName];
  return pageName ? `${pageName} | eQuote` : 'eQuote - Minh họa Bảo hiểm Số';
};

const getInitialRoute = (): keyof RootStackParamList => {
  if (typeof window === 'undefined') return AppRoute.M03HomeScreen;
  const path = window.location.pathname;
  if (path.includes('/login')) return AppRoute.M02LoginScreen;
  if (path.includes('/loading')) return AppRoute.M01SplashScreen;
  if (path.includes('/trang-chu')) return AppRoute.M03HomeScreen;
  if (path.includes('/nhap-tt-kh')) return AppRoute.M06InputCustomerScreen;
  if (path.includes('/nhap-minh-hoa') || path.includes('/nhap-tai-lieu-minh-hoa') || path.includes('/cau-hinh')) return AppRoute.M06ConfigureProductScreen;
  if (path.includes('/ket-qua')) return AppRoute.M07ResultScreen;
  if (path.includes('/tinh-nhanh')) return AppRoute.M04ProductListScreen;
  if (path.includes('/chi-tiet-san-pham')) return AppRoute.M05ProductDetailScreen;
  if (path.includes('/tao-minh-hoa')) return AppRoute.M06InputCustomerScreen;
  if (path.includes('/quick-propose/chartView')) return AppRoute.M07ResultScreen;
  if (path.includes('/so-sanh')) return AppRoute.M08CompareScreen;
  if (path.includes('/pdf')) return AppRoute.M09PdfScreen;
  if (path.includes('/luu-khach-hang')) return AppRoute.M10SaveCustomerScreen;
  if (path.includes('/minh-hoa') || path.includes('/tai-lieu-minh-hoa')) return AppRoute.M11HistoryScreen;
  if (path.includes('/khach-hang/') && path.split('/khach-hang/')[1]?.length > 0) return AppRoute.M16CustomerDetailScreen;
  if (path.includes('/quan-ly-khach-hang/') && path.split('/quan-ly-khach-hang/')[1]?.length > 0) return AppRoute.M16CustomerDetailScreen;
  if (path.includes('/khach-hang') || path.includes('/quan-ly-khach-hang')) return AppRoute.M15CustomerListScreen;
  if (path.includes('/chi-tiet-khach-hang')) return AppRoute.M16CustomerDetailScreen;

  if (path.includes('/khao-sat-nhu-cau')) return AppRoute.M17FinancialSurveyScreen;
  if (path.includes('/ho-so')) return AppRoute.M12ProfileScreen;
  if (path.includes('/share')) return AppRoute.M13ShareScreen;
  if (path.includes('/crm-notes')) return AppRoute.M14NotesScreen;
  return AppRoute.M03HomeScreen;
};

const ROUTE_KEYWORDS = new Set([
  'nhap-tt-kh',
  'nhap-minh-hoa',
  'nhap-tai-lieu-minh-hoa',
  'ket-qua',
  'step1',
  'tinh-nhanh',
  'khach-hang',
  'quan-ly-khach-hang',
  'minh-hoa',
  'tai-lieu-minh-hoa',
  'trang-chu',
  'so-sanh',
  'pdf',
  'luu-khach-hang',
  'ho-so',
  'khao-sat-nhu-cau',
]);

const getInitialParams = (): any => {
  if (typeof window === 'undefined') return undefined;
  const path = window.location.pathname;
  const parts = path.split('/').filter(Boolean);

  if ((path.includes('/khach-hang/') || path.includes('/quan-ly-khach-hang/')) && parts.length >= 2) {
    const lastPart = parts[parts.length - 1];
    if (!ROUTE_KEYWORDS.has(lastPart)) {
      return { customerId: lastPart };
    }
  }

  if (path.includes('/minh-hoa/') || path.includes('/tinh-nhanh/') || path.includes('/tai-lieu-minh-hoa/')) {
    const idx = parts.findIndex(p => p === 'minh-hoa' || p === 'tinh-nhanh' || p === 'tai-lieu-minh-hoa');
    if (idx !== -1 && idx + 1 < parts.length) {
      const candidate = parts[idx + 1];
      if (!ROUTE_KEYWORDS.has(candidate)) {
        return { illustrationId: candidate };
      }
    }
  }
  return undefined;
};

interface DesktopSidebarProps {
  currentRoute: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  getActiveTab: (route: string) => string;
  onNavigate: (route: keyof RootStackParamList) => void;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  currentRoute,
  isCollapsed,
  onToggleCollapse,
  getActiveTab,
  onNavigate,
}) => {
  return (
    <View style={[styles.sidebar, isCollapsed && styles.sidebarCollapsed]}>
      <View style={[styles.sidebarBrand, isCollapsed && styles.sidebarBrandCentered]}>
        {isCollapsed ? (
          <LogoIcon size={36} style={{ background: 'white', borderRadius: 8 }} />
        ) : (
          <>
            <LogoIcon size={32} style={{ background: 'white', borderRadius: 8 }} />
            <Text style={styles.brandText}>
              <Text style={{ color: '#00A651' }}>e</Text>Quote
            </Text>
          </>
        )}
      </View>

      <View style={styles.sidebarMenu}>
        {SIDEBAR_ITEMS.map((item) => {
          const isActive = getActiveTab(currentRoute) === item.id;
          return (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.sidebarItem,
                isActive && styles.sidebarItemActive,
                isCollapsed && styles.sidebarItemCentered,
              ]}
              onPress={() => onNavigate(item.id as keyof RootStackParamList)}
              {...(isCollapsed ? ({ title: item.label } as any) : {})}
            >
              <Icon name={item.icon} size={22} color={isActive ? '#00A651' : '#cbd5e1'} />
              {!isCollapsed && (
                <Text style={[styles.sidebarItemText, isActive && styles.sidebarItemActiveText]}>
                  {item.label}
                </Text>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      <TouchableOpacity
        style={[styles.sidebarToggleBottom, isCollapsed && styles.sidebarItemCentered]}
        onPress={onToggleCollapse}
        activeOpacity={0.8}
        {...(isCollapsed ? ({ title: 'Mở rộng menu' } as any) : {})}
      >
        <Icon name={isCollapsed ? 'chevron_right' : 'chevron_left'} size={22} color="#cbd5e1" />
        {!isCollapsed && <Text style={styles.toggleText}>Thu gọn menu</Text>}
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.sidebarLogout, isCollapsed && styles.sidebarLogoutCentered]}
        onPress={() => onNavigate(AppRoute.M02LoginScreen)}
        {...(isCollapsed ? ({ title: 'Thoát' } as any) : {})}
      >
        <Icon name="logout" size={22} color="#f87171" />
        {!isCollapsed && <Text style={styles.logoutText}>Thoát</Text>}
      </TouchableOpacity>
    </View>
  );
};

const getBreadcrumbs = (currentScreen: string, illustrationInput?: any): { parent: string; current: string } => {
  const illId = illustrationInput?.illustrationId;
  const custName = illustrationInput?.customerName;
  const label = illId
    ? `${illId}${custName ? ` - ${custName}` : ''}`
    : custName || '';

  switch (currentScreen) {
    case AppRoute.M04ProductListScreen:
      return { parent: 'Tính nhanh TLMH', current: 'Lựa chọn sản phẩm' };
    case AppRoute.M05ProductDetailScreen:
      return { parent: 'Tính nhanh TLMH', current: 'Chi tiết sản phẩm' };
    case AppRoute.M06InputCustomerScreen:
    case AppRoute.M06SelectCustomerScreen:
      return { parent: 'Tính nhanh TLMH', current: 'Nhập thông tin khách hàng' };
    case AppRoute.M06ConfigureProductScreen:
      return {
        parent: 'Tính nhanh TLMH',
        current: label ? `Cấu hình TLMH (${label})` : 'Sản phẩm Bảo hiểm Liên kết chung Xanh Ước Mơ',
      };
    case AppRoute.M07ResultScreen:
      return {
        parent: 'Tính nhanh TLMH',
        current: label ? `Kết quả minh họa (${label})` : 'Kết quả minh họa & dòng tiền',
      };
    case AppRoute.M08CompareScreen:
      return { parent: 'Tính nhanh TLMH', current: 'So sánh phương án' };
    case AppRoute.M09PdfScreen:
      return { parent: 'Tính nhanh TLMH', current: 'Tài liệu PDF' };
    case AppRoute.M10SaveCustomerScreen:
      return { parent: 'Tính nhanh TLMH', current: 'Lưu khách hàng' };
    case AppRoute.M11HistoryScreen:
      return { parent: 'Tài liệu minh họa', current: 'Danh sách bảng minh họa đã tạo' };
    case AppRoute.M15CustomerListScreen:
      return { parent: 'Quản lý khách hàng', current: 'Danh sách Bên mua BH' };
    case AppRoute.M16CustomerDetailScreen:
      return { parent: 'Quản lý khách hàng', current: 'Chi tiết khách hàng' };
    case AppRoute.M17FinancialSurveyScreen:
      return { parent: 'Khảo sát nhu cầu', current: 'Phân tích nhu cầu tài chính' };
    default:
      return { parent: 'eQuote', current: 'Trang chủ' };
  }
};

const DesktopHeader: React.FC<{
  currentRoute: string;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onNavigate: (route: keyof RootStackParamList) => void;
}> = ({ currentRoute, isSidebarCollapsed, onToggleSidebar, onNavigate }) => {
  const illustrationInput = useAtomValue(illustrationInputAtom);
  const breadcrumb = getBreadcrumbs(currentRoute, illustrationInput);

  return (
    <View style={styles.desktopHeaderCompact}>
      {/* Left items: Hamburger Menu + Avatar Badge */}
      <View style={styles.headerLeftSection}>
        <TouchableOpacity style={styles.hamburgerBtn} onPress={onToggleSidebar} activeOpacity={0.7}>
          <Icon name="menu" size={20} color="#1e293b" />
        </TouchableOpacity>

        <View style={styles.dividerVertical} />

        <View style={styles.avatarBadgeCircle}>
          <Text style={styles.avatarBadgeText}>BH</Text>
        </View>

        <View style={styles.dividerVertical} />
      </View>

      {/* Center Breadcrumbs */}
      <View style={styles.breadcrumbContainer}>
        <Text style={styles.breadcrumbParentText}>{breadcrumb.parent}</Text>
        <Icon name="chevron_right" size={16} color="#475569" style={styles.breadcrumbChevron} />
        <Text style={styles.breadcrumbCurrentText} numberOfLines={1}>
          {breadcrumb.current}
        </Text>
      </View>

      {/* Right Header Actions */}
      <View style={styles.desktopHeaderActions}>
        <TouchableOpacity
          style={styles.headerActionBtn}
          onPress={() => onNavigate(AppRoute.M14NotesScreen)}
          activeOpacity={0.7}
        >
          <Icon name="comment" size={16} color="#475569" />
          <Text style={styles.headerActionText}>Ghi chú</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.headerReminderPillCompact} activeOpacity={0.8}>
          <Icon name="notifications" size={16} color={colors.primary} />
          <Text style={styles.headerReminderTextCompact}>0 Lời nhắc</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

/**
 * AppShell - renders bên trong NavigationContainer để dùng được useNavigation().
 * Đây là pattern chuẩn: mọi component cần navigate phải là descendant của NavigationContainer.
 */
const AppShell: React.FC = () => {
  const navigation = useNavigation<any>();
  const initialRouteRef = useRef<keyof RootStackParamList>(getInitialRoute());
  const [currentRoute, setCurrentRoute] = useState<string>(initialRouteRef.current);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const { width } = useWindowDimensions();

  const isDesktop = width >= 1024;
  const showHeader = currentRoute !== AppRoute.M01SplashScreen && currentRoute !== AppRoute.M02LoginScreen && currentRoute !== AppRoute.M13ShareScreen;
  const showFooter = currentRoute !== AppRoute.M01SplashScreen && currentRoute !== AppRoute.M02LoginScreen && currentRoute !== AppRoute.M13ShareScreen;

  // navigate dùng useNavigation() hook - reliable trên web, không bị linking conflict
  const handleNavigate = useCallback((routeName: keyof RootStackParamList, params?: any) => {
    navigation.navigate(routeName as any, params);
  }, [navigation]);

  // Lắng nghe popstate (browser Back/Forward button)
  // Khi user bấm Back, URL thay đổi → đọc URL mới → navigate đến screen tương ứng
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof document !== 'undefined') {
      document.title = getDocumentTitle(initialRouteRef.current);
    }
    const handlePopState = () => {
      const routeName = getInitialRoute(); // map pathname → screen name
      navigation.navigate(routeName as any);
      if (typeof document !== 'undefined') {
        document.title = getDocumentTitle(routeName);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigation]);

  const getActiveTab = (currentScreen: string): string => {
    if (currentScreen === AppRoute.M03HomeScreen) return AppRoute.M03HomeScreen;
    if (
      [
        AppRoute.M04ProductListScreen,
        AppRoute.M05ProductDetailScreen,
        AppRoute.M06InputCustomerScreen,
        AppRoute.M07ResultScreen,
        AppRoute.M08CompareScreen,
        AppRoute.M09PdfScreen,
        AppRoute.M10SaveCustomerScreen,
      ].includes(currentScreen as AppRoute)
    ) {
      return AppRoute.M04ProductListScreen;
    }
    if (currentScreen === AppRoute.M11HistoryScreen) return AppRoute.M11HistoryScreen;
    if (currentScreen === AppRoute.M15CustomerListScreen) return AppRoute.M15CustomerListScreen;
    if (currentScreen === AppRoute.M16CustomerDetailScreen) return AppRoute.M15CustomerListScreen;
    if (currentScreen === AppRoute.M12ProfileScreen) return AppRoute.M12ProfileScreen;
    return '';
  };

  const getHeaderTitle = (currentScreen: string): string => {
    const titleMap: Record<string, string> = {
      [AppRoute.M03HomeScreen]: 'Trang chủ',
      [AppRoute.M04ProductListScreen]: 'Lựa chọn sản phẩm và tạo Tài liệu minh họa bán hàng',
      [AppRoute.M05ProductDetailScreen]: 'Chi tiết sản phẩm',
      [AppRoute.M06InputCustomerScreen]: 'Tạo minh họa',
      [AppRoute.M07ResultScreen]: 'Kết quả minh họa',
      [AppRoute.M08CompareScreen]: 'So sánh phương án',
      [AppRoute.M09PdfScreen]: 'Tài liệu PDF',
      [AppRoute.M10SaveCustomerScreen]: 'Lưu khách hàng',
      [AppRoute.M11HistoryScreen]: 'Danh sách tài liệu minh họa bán hàng đã tạo',
      [AppRoute.M15CustomerListScreen]: 'Danh sách Bên mua bảo hiểm và Người phụ thuộc',
      [AppRoute.M16CustomerDetailScreen]: 'Chi tiết khách hàng',
      [AppRoute.M17FinancialSurveyScreen]: 'Khảo sát nhu cầu tài chính',
      [AppRoute.M12ProfileScreen]: 'Cá nhân & Cấu hình',
    };
    return titleMap[currentScreen] || 'eQuote';
  };

  return (
    <View style={isDesktop ? styles.desktopShell : styles.shell}>
      {/* Sidebar - desktop only */}
      {isDesktop && showHeader && (
        <DesktopSidebar
          currentRoute={currentRoute}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          getActiveTab={getActiveTab}
          onNavigate={handleNavigate}
        />
      )}

      {/* Main Content Area */}
      <View style={isDesktop ? styles.desktopContent : styles.mobileContentContainer}>
        {/* Header */}
        {isDesktop ? (
          showHeader && (
            <DesktopHeader
              currentRoute={currentRoute}
              isSidebarCollapsed={isSidebarCollapsed}
              onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              onNavigate={handleNavigate}
            />
          )
        ) : (
          showHeader && <AppBar />
        )}

        {/* Stack Navigator */}
        <View
          style={[
            isDesktop ? styles.desktopMain : styles.content,
            !isDesktop && {
              paddingTop: showHeader ? APP_BAR_HEIGHT : 0,
              paddingBottom: showFooter ? BOTTOM_NAV_HEIGHT : 0,
            }
          ]}
        >
          <Stack.Navigator
            initialRouteName={initialRouteRef.current}
            screenOptions={{
              headerShown: false,
              animationEnabled: false,
              cardStyle: { backgroundColor: '#f5f7fa' },
            }}
            screenListeners={{
              state: (e) => {
                // Cập nhật currentRoute khi Stack thay đổi
                const state = e.data?.state;
                if (state) {
                  let route: any = state.routes[state.index];
                  while (route.state) {
                    const nestedIndex = route.state.index ?? 0;
                    route = route.state.routes[nestedIndex];
                  }
                  const name = route.name as keyof RootStackParamList;
                  setCurrentRoute(name);

                  // Update HTML document.title dynamically for web
                  if (typeof document !== 'undefined') {
                    document.title = getDocumentTitle(name);
                  }

                  // Dynamic RESTful URL Syncing
                  let targetPath = ROUTE_PATH_MAP[name];
                  if (name === AppRoute.M07ResultScreen) {
                    const illId = route.params?.illustrationId;
                    targetPath = illId ? `/minh-hoa/${illId}/ket-qua` : '/minh-hoa/ket-qua';
                  } else if (name === AppRoute.M06InputCustomerScreen || name === AppRoute.M06SelectCustomerScreen) {
                    const illId = route.params?.illustrationId;
                    targetPath = illId ? `/minh-hoa/${illId}/nhap-tt-kh` : '/minh-hoa/nhap-tt-kh';
                  } else if (name === AppRoute.M06ConfigureProductScreen) {
                    const illId = route.params?.illustrationId;
                    targetPath = illId ? `/minh-hoa/${illId}/nhap-minh-hoa` : '/minh-hoa/nhap-minh-hoa';
                  } else if (name === AppRoute.M16CustomerDetailScreen) {
                    const custId = route.params?.customerId;
                    targetPath = custId ? `/khach-hang/${custId}` : '/khach-hang';
                  }


                  if (targetPath && typeof window !== 'undefined' && window.history) {
                    if (window.location.pathname !== targetPath) {
                      window.history.pushState({}, '', targetPath);
                    }
                  }
                }
              },
            }}
          >
            <Stack.Screen name={AppRoute.M01SplashScreen}          component={M01SplashScreen} />
            <Stack.Screen name={AppRoute.M02LoginScreen}           component={M02LoginScreen} />
            <Stack.Screen name={AppRoute.M03HomeScreen}            component={M03HomeScreen} />
            <Stack.Screen name={AppRoute.M04ProductListScreen}     component={M04ProductListScreen} />
            <Stack.Screen name={AppRoute.M05ProductDetailScreen}   component={M05ProductDetailScreen} />
            <Stack.Screen name={AppRoute.M06SelectCustomerScreen}  component={M06SelectCustomerScreen} initialParams={getInitialParams()} />
            <Stack.Screen name={AppRoute.M06ConfigureProductScreen} component={M06ConfigureProductScreen} initialParams={getInitialParams()} />
            <Stack.Screen name={AppRoute.M06InputCustomerScreen}   component={M06InputCustomerScreen} initialParams={getInitialParams()} />
            <Stack.Screen name={AppRoute.M07ResultScreen}          component={M07ResultScreen} initialParams={getInitialParams()} />
            <Stack.Screen name={AppRoute.M08CompareScreen}         component={M08CompareScreen} />
            <Stack.Screen name={AppRoute.M09PdfScreen}             component={M09PdfScreen} />
            <Stack.Screen name={AppRoute.M10SaveCustomerScreen}    component={M10SaveCustomerScreen} />
            <Stack.Screen name={AppRoute.M11HistoryScreen}         component={M11HistoryScreen} />
            <Stack.Screen name={AppRoute.M12ProfileScreen}         component={M12ProfileScreen} />
            <Stack.Screen name={AppRoute.M13ShareScreen}           component={M13ShareScreen} />
            <Stack.Screen name={AppRoute.M14NotesScreen}           component={M14NotesScreen} />
            <Stack.Screen name={AppRoute.M15CustomerListScreen}    component={M15CustomerListScreen} />
            <Stack.Screen name={AppRoute.M16CustomerDetailScreen}  component={M16CustomerDetailScreen} initialParams={getInitialParams()} />
            <Stack.Screen name={AppRoute.M17FinancialSurveyScreen} component={M17FinancialSurveyScreen} />
          </Stack.Navigator>
        </View>

        {/* Footer - mobile only */}
        {!isDesktop && showFooter && <BottomNav />}
      </View>
    </View>
  );
};

/**
 * AppNavigator - chỉ là wrapper NavigationContainer.
 * Toàn bộ UI và navigation logic nằm trong AppShell (bên trong container).
 */
export const AppNavigator: React.FC = () => {
  return (
    <NavigationContainer
      ref={navigationRef}
      documentTitle={{
        enabled: true,
        formatter: (_options, route) => getDocumentTitle(route?.name || ''),
      }}
      onReady={() => {
        const current = navigationRef.getCurrentRoute();
        if (current) {
          if (typeof document !== 'undefined') {
            document.title = getDocumentTitle(current.name);
          }
          // URL sync on initial load preserving dynamic params
          const name = current.name as keyof RootStackParamList;
          const params: any = current.params;
          let targetPath = ROUTE_PATH_MAP[name];

          if (name === AppRoute.M07ResultScreen) {
            const illId = params?.illustrationId;
            targetPath = illId ? `/minh-hoa/${illId}/ket-qua` : '/minh-hoa/ket-qua';
          } else if (name === AppRoute.M06InputCustomerScreen || name === AppRoute.M06SelectCustomerScreen) {
            const illId = params?.illustrationId;
            targetPath = illId ? `/minh-hoa/${illId}/nhap-tt-kh` : '/minh-hoa/nhap-tt-kh';
          } else if (name === AppRoute.M06ConfigureProductScreen) {
            const illId = params?.illustrationId;
            targetPath = illId ? `/minh-hoa/${illId}/nhap-minh-hoa` : '/minh-hoa/nhap-minh-hoa';
          } else if (name === AppRoute.M16CustomerDetailScreen) {
            const custId = params?.customerId;
            targetPath = custId ? `/khach-hang/${custId}` : '/khach-hang';
          }

          if (targetPath && typeof window !== 'undefined' && window.history) {
            if (window.location.pathname !== targetPath) {
              window.history.replaceState({}, '', targetPath);
            }
          }
        }
      }}
    >
      <AppShell />
    </NavigationContainer>
  );
};
